import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordNormalized,
} from "vue-router";
import { getStore } from "@/store";
import { captureFlags } from "@/flags";
import { markRaw } from "vue";

// Simplified version of the vue-router.RouteLocationNormalized object limited
// to the functionality needed in the checkLoggedIn guard function.
export interface SimpleRoute {
  path: string;
  fullPath: string;
}

/**
 * Executes before changing routes and confirms that the user has an unexpired
 * JWT. If no JWT exists, or the JWT is expired, the user will be redirected to
 * the login route with the original path stored at the end of the login path
 * for login redirects.
 *
 * https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
 * @param to - route the user requested navigation to.
 * @param from - current route that the user is leaving.
 * @param next - resolution function to call depending on how the redirection
 *   should be carried out.
 */
export function checkLoggedIn(
  to: SimpleRoute,
  from: SimpleRoute | null | undefined,
  next: NavigationGuardNext
): void {
  // Update flags with anything newly added.
  captureFlags();

  const token = getStore().getters["user/getToken"];
  const loggedIn = getStore().getters["user/isLoggedIn"];
  const toLogin = to.path.startsWith("/login");
  const toSignup = to.path.startsWith("/signup");
  const toDocs = to.path.startsWith("/docs");
  const onboardingRedirect = to.path.includes("/guide");

  if (loggedIn && (toLogin || toSignup)) {
    next({ name: "Checking for Data" });
  } else if (!loggedIn && toDocs) {
    next();
  } else if (!loggedIn && onboardingRedirect) {
    next(`/docs/guides${to.path.replace("/guide", "")}`);
  } else if (!loggedIn && !(toLogin || toSignup)) {
    sessionStorage.setItem("redirect_url", to.fullPath);
    // With the broadcast channel, we can end up in this situation
    // where, we don't have a token but soon will.
    if (token) {
      getStore().dispatch("user/logUserOut");
    } else {
      // When we open the tab for the first time, and still no broadcast
      // happens between tabs
      next("/login");
    }
  } else {
    next();
  }
}

/**
 * Executes before changing routes into an /admin based path and confirms that
 * the user is an admin. If they aren't an admin, redirect to '/'. The above
 * guard will redirect them to login if needed.
 *
 * https://router.vuejs.org/guide/advanced/navigation-guards.html#per-route-guard
 * @param to - route the user requested navigation to.
 * @param from - current route that the user is leaving.
 * @param next - resolution function to call depending on how the redirection
 *   should be carried out.
 */
export function checkAdmin(
  to: SimpleRoute,
  from: SimpleRoute | null | undefined,
  next: NavigationGuardNext
): void {
  const isAdmin = getStore().getters["user/isAdmin"];

  if (isAdmin) {
    next();
  } else {
    next("/");
  }
}

/**
 * Decides whether to redirect to the specified route or to add the resolved
 * component to the Drawer lists of components to be rendered inside the Drawer.
 * @param guard - route the user requested navigation to.
 * @returns - False when navigations need to be cancelled, true otherwise.
 */
export function checkDrawerNavigation(guard: RouteLocationNormalized): boolean {
  if (getStore().getters["docs/getUpdate"]) {
    // Keeps hash routing away from messing up the Drawer navigation
    if (guard.hash) return false;

    const componentToRender = (guard.matched.at(-1) as RouteRecordNormalized)
      .components?.default;
    getStore().commit(
      "docs/pushDrawerComponent",
      markRaw(componentToRender || {})
    );

    // Cancel main navigation and render component in the Drawer
    return false;
  }
  return true;
}

/**
 * Verifies that there's data available for the current Account.
 * if not, it redirects back to Checking for Data view.
 * @param to - route the user requested navigation to.
 * @param from - current route that the user is leaving.
 * @returns True or Checking for Data route to redirect
 */
export function checkDataFetched(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized | null | undefined
): boolean | RouteLocationRaw {
  // Service Group view is currently `Home` view, but needs to be changed during this cycle
  const blockedViews = [
    "Logs",
    "Traces",
    "Spans",
    "Metrics",
    "Alerting",
    // TODO: Fix names below once Home default route has been changed
    "Service Group",
    "Home",
  ];
  if (blockedViews.includes(to.name as string)) {
    const store = getStore();
    const sgId = store.getters["user/getAppgroupId"];
    const sgLocalStoredData = localStorage.getItem(sgId);
    const sgParsedData = JSON.parse(sgLocalStoredData || "{}");

    if (!sgLocalStoredData || sgParsedData.expiresAt < Date.now()) {
      localStorage.removeItem(sgId);
      return { name: "Checking for Data" };
    }
  }

  return true;
}
